<template>
  <div v-if="entries.length > 0" class="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
    <!-- expanded: every download across projects, newest first -->
    <div
      v-if="expanded"
      class="w-96 max-w-[calc(100vw-3rem)] max-h-112 overflow-y-auto rounded-xl border border-app-gray-200 bg-app-white shadow-lg dark:border-app-gray-700 dark:bg-app-dark-surface2"
    >
      <div class="flex items-center justify-between px-4 py-3 border-b border-app-gray-100 dark:border-app-gray-700">
        <span class="text-sm font-semibold text-app-gray-900 dark:text-app-white">Downloads</span>
        <Button variant="link" size="xs" class="p-0! text-xs!" aria-label="Clear all downloads" @click="clearAll">Clear all</Button>
      </div>

      <ul class="divide-y divide-app-gray-100 dark:divide-app-gray-700">
        <li v-for="entry in entries" :key="entry.id" class="flex items-start gap-2 px-4 py-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-1">
              <p class="text-sm font-medium text-app-gray-900 dark:text-app-white truncate">{{ entry.projectName || entry.projectId }}</p>
              <CustomTooltip placement="top" theme="dark" :classes="{ tooltip: '!text-left !py-2' }">
                <Icon name="information-circle-solid-icon" size="14" class="shrink-0 text-app-gray-400 cursor-help" />
                <template #content>
                  <div class="flex flex-col gap-1 min-w-40 max-w-64">
                    <div v-for="detail in details(entry)" :key="detail.label" class="flex justify-between gap-3 text-xs">
                      <span class="text-app-gray-400 whitespace-nowrap">{{ detail.label }}</span>
                      <span class="text-app-white font-medium break-all text-right">{{ detail.value }}</span>
                    </div>
                  </div>
                </template>
              </CustomTooltip>
            </div>
            <p class="text-xs text-app-gray-400">{{ formatDate(entry) }}</p>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center justify-end gap-1">
              <a
                v-if="entry.status === 'completed' && entry.downloadUrl"
                :href="entry.downloadUrl"
                target="_blank"
                rel="noopener"
                class="shrink-0 text-app-gray-400 hover:text-app-gray-700 dark:hover:text-app-white"
                aria-label="Download CSV"
              >
                <Icon name="download-solid-icon" size="14" />
              </a>
              <button
                v-if="entry.status === 'failed' || entry.status === 'expired'"
                type="button"
                class="shrink-0 text-app-gray-400 hover:text-app-gray-700 dark:hover:text-app-white"
                aria-label="Retry export"
                @click="retry(entry)"
              >
                <Icon name="refresh-icon" size="14" />
              </button>
              <button
                type="button"
                class="shrink-0 text-app-gray-400 hover:text-app-gray-700 dark:hover:text-app-white"
                aria-label="Clear download"
                @click="clearOne(entry.id)"
              >
                <Icon name="x-solid-icon" size="14" />
              </button>
            </div>

            <!-- exporting -->
            <div v-if="entry.status === 'exporting'" class="flex items-center gap-2 text-xs text-app-gray-500">
              <svg class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>{{ progressLabel(entry) }}</span>
            </div>

            <!-- completed -->
            <div v-else-if="entry.status === 'completed'" class="flex flex-col gap-1">
              <span v-if="expiryNote(entry)" class="text-xs text-app-gray-500">{{ expiryNote(entry) }}</span>
            </div>

            <!-- failed -->
            <div v-else-if="entry.status === 'failed'" class="flex flex-col gap-1">
              <span class="text-xs text-app-error">{{ entry.errorMessage || "Export failed" }}</span>
            </div>

            <!-- expired -->
            <div v-else class="flex items-center gap-2">
              <span class="text-xs text-app-warning">Export link expired</span>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- action button: toggles the list, shows progress while exporting -->
    <button
      type="button"
      class="relative flex h-12 w-12 items-center justify-center rounded-full border border-app-gray-200 bg-app-white shadow-lg hover:bg-app-gray-50 dark:border-app-gray-700 dark:bg-app-dark-surface2"
      :aria-label="expanded ? 'Hide downloads' : 'Show downloads'"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <svg
        v-if="activeCount > 0"
        class="absolute h-12 w-12 animate-spin text-app-blue-500"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <Icon name="download-solid-icon" class="text-app-gray-900 dark:text-app-white" />
      <span
        v-if="activeCount > 0"
        class="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-app-blue-500 px-1 text-xxs font-semibold text-app-white"
      >
        {{ activeCount }}
      </span>
    </button>
  </div>
</template>

<script lang="ts">
import { Icon } from "@toruslabs/vue-components/Icon";
import { defineComponent } from "vue";

import { Button } from "@/components/Button";
import CustomTooltip from "@/components/CustomTooltip";
import { downloadsModule } from "@/store";
import { type DownloadEntry } from "@/store/modules/downloads";

export default defineComponent({
  name: "DownloadsOverlay",
  components: { Button, CustomTooltip, Icon },
  data: () => ({
    expanded: false,
    // Reactive clock so the expiry countdown re-renders periodically while a
    // completed download link is on screen.
    nowTs: Date.now(),
    clockTimer: null as ReturnType<typeof setInterval> | null,
    // Ids already reflected in the panel; a brand-new id triggers auto-expand.
    seenIds: [] as string[],
    // Downloads restored from storage predate this mount, so we only auto-open
    // for exports started after the overlay mounted (i.e. this session).
    mountTs: Date.now(),
  }),
  computed: {
    entries(): DownloadEntry[] {
      return downloadsModule.allEntriesSorted;
    },
    activeCount(): number {
      return downloadsModule.activeCount;
    },
  },
  watch: {
    entries: {
      immediate: true,
      handler(entries: DownloadEntry[]) {
        const hasNewDownload = entries.some((entry) => !this.seenIds.includes(entry.id) && entry.startedAt >= this.mountTs);
        if (hasNewDownload) this.expanded = true;
        this.seenIds = entries.map((entry) => entry.id);
      },
    },
  },
  created() {
    this.clockTimer = setInterval(() => {
      this.nowTs = Date.now();
    }, 30000);
  },
  beforeUnmount() {
    if (this.clockTimer !== null) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  },
  methods: {
    progressLabel(entry: DownloadEntry): string {
      if (entry.totalRows > 0) {
        const percent = Math.min(100, Math.max(0, Math.floor((entry.processedRows / entry.totalRows) * 100)));
        return `Exporting ${percent}%`;
      }
      return "Exporting...";
    },
    formatDateValue(value: string): string {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return parsed.toLocaleString();
    },
    details(entry: DownloadEntry): { label: string; value: string }[] {
      const rows: { label: string; value: string }[] = [{ label: "Export", value: entry.label }];
      const { userIdOrAddress, lastLoginFrom, lastLoginTo } = entry.filters;
      if (userIdOrAddress) rows.push({ label: "User ID / Address", value: userIdOrAddress });
      if (lastLoginFrom) rows.push({ label: "Last login from", value: this.formatDateValue(lastLoginFrom) });
      if (lastLoginTo) rows.push({ label: "Last login to", value: this.formatDateValue(lastLoginTo) });
      if (!userIdOrAddress && !lastLoginFrom && !lastLoginTo) rows.push({ label: "Filters", value: "All users" });
      return rows;
    },
    formatDate(entry: DownloadEntry): string {
      const ts = entry.completedAt ?? entry.startedAt;
      return new Date(ts).toLocaleString();
    },
    expiryNote(entry: DownloadEntry): string {
      if (!entry.downloadExpiresAt) return "";
      const expiresAt = new Date(entry.downloadExpiresAt).getTime();
      if (Number.isNaN(expiresAt)) return "";
      const msLeft = Math.max(0, expiresAt - this.nowTs);
      if (msLeft < 60000) {
        return "Link expires soon (<1min)";
      }
      const minutes = Math.round(msLeft / 60000);
      return `Link expires in ~${minutes} min`;
    },
    retry(entry: DownloadEntry): void {
      // A retry replaces the failed/expired attempt rather than piling up beside it.
      downloadsModule.clearOne(entry.id);
      void downloadsModule.startUsersExport({
        projectId: entry.projectId,
        projectName: entry.projectName,
        teamId: entry.teamId,
        filters: entry.filters,
        label: entry.label,
      });
    },
    clearOne(id: string): void {
      downloadsModule.clearOne(id);
    },
    clearAll(): void {
      downloadsModule.clearAll();
    },
  },
});
</script>
